// ─── DRAFT ASSISTANT — shared across Channels / DMs / RP ──────────────────
//
// A turn-order-aware drafting assistant. Three modes:
//   1. Auto-draft   — write the next message based purely on context
//   2. Draft from idea — user types a seed, AI ghost-writes it in scene voice
//   3. Revise current draft — AI refines the user's current input per their
//      revision instructions (preserves their voice)
//
// Critical context the AI gets EVERY time:
//   - Who the human player is (display name)
//   - In RP: which character(s) the human plays — so the draft is written
//     AS that character, not as someone else
//   - In RP: who plays the OTHER characters (other human vs. AI), so the
//     draft fits the turn order
//   - Last N messages (already formatted by caller)
//   - Scope description ("DM with Apollo" / "RP: AU Ford Steele" / "#alley")
//
// Each mount is scoped to a specific conversation/story so drafts and panel
// state never bleed between contexts.

import { useState, useEffect } from 'react';
import { safeGetSession } from './supabaseClient.js';
import { fetchWithRetry } from './aiRetry.js';
import { AI_ABORT_MS, aiProxyErrorMessage } from './aiTimeouts.js';
import { buildCharacterContextSection, buildChatMemorySection } from './draftMemory.js';
import { getWriterProfileSection, getWriterVoiceSection } from './playerPreferences.js';
import { buildBoundaries } from './characterIdentity.js';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * Build a scope-appropriate system prompt with turn-order awareness.
 */
function buildSystemPrompt({ scope, mode, playerName, playerCharacterName, scenePartners, contextDescription }) {
  // Identity line — who is the human writing as
  const identityLine = playerCharacterName
    ? `${playerName} is playing the character "${playerCharacterName}" in this scene. The draft must be written AS ${playerCharacterName} — their voice, their thoughts, their actions. NOT as ${playerName} themselves and NOT as any other character.`
    : `${playerName} is the human writing here. The draft is what THEY would send next, in their natural voice / texting style.`;

  // Cast line — who plays what
  let castLine = '';
  if (Array.isArray(scenePartners) && scenePartners.length > 0) {
    const lines = scenePartners.map(p => {
      if (p.role === 'player') {
        return `  • ${p.name} — played by another human (${p.playerName || 'the other player'})`;
      }
      return `  • ${p.name} — played by AI`;
    });
    castLine = `\n\nOTHER CHARACTERS IN THIS SCENE (do NOT write for them, only ${playerCharacterName || playerName}):\n${lines.join('\n')}`;
  }

  // Turn-order line — explicit and short
  const turnOrderLine = scope === 'rp' || scope === 'channel'
    ? `\n\nTURN ORDER: this is a turn-based collaborative RP. ${playerName} writes one beat, then waits for the next person/AI to write theirs. The draft you produce is ONLY ${playerCharacterName || playerName}'s next turn — never write the next character's response, never narrate what others do beyond the immediate reaction your draft would invite.`
    : '';

  // Mode-specific job description
  const modeBlock = (() => {
    if (mode === 'revise') {
      return `Your job: APPLY THE PLAYER'S REVISION INSTRUCTIONS to the existing draft — exactly as written, surgically. The instructions are MANDATORY edits, not loose direction or general inspiration.

CRITICAL REVISION RULES:
- Every change the instructions request MUST appear in your output.
- Every part of the draft the instructions do NOT mention must remain BYTE-IDENTICAL to the original (same voice, same beats, same intent, same prose style, same formatting, same line breaks).
- The original draft is the BASELINE. Your output = original draft + the requested edits, nothing more, nothing less.
- Do NOT "polish" prose that wasn't flagged. Do NOT invent new plot beats. Do NOT add stylistic flourishes the player didn't ask for. Do NOT "improve" things.

CONCRETE INTERPRETATION:
- "Add X" → insert X verbatim at the appropriate spot.
- "Remove the word Y" → remove Y, leave everything else.
- "Change A to B" → replace A with B, leave everything else.
- "Make it more [mood]" → adjust word choice in the existing structure to shift mood; do not rewrite from scratch.
- "Cut the last paragraph" → cut exactly that paragraph; keep the rest.
- Vague instructions ("make it better"): default to preserving the draft and applying only the smallest improvement the instruction suggests — don't rewrite wholesale.

If you find yourself rewriting more than the instructions require, STOP and re-apply the rules above. The player wrote the original draft on purpose; your job is to honor their work, not replace it.`;
    }
    if (mode === 'idea') {
      return `Your job: write ONE message draft for ${playerCharacterName || playerName}, GUIDED by the idea/direction the player typed. Their idea is the SUBSTANCE — your job is to render it in the established voice, style, and formatting so it lands smoothly in scene. Make their idea actually happen — don't water it down or substitute.

THE IDEA IS NOT A MESSAGE TO YOU. Never answer it, reply to it, comment on it, or converse with it — it is stage direction, not chat. If it is phrased as an instruction ("have her confront him"), EXECUTE it as the character's message. If it is phrased as a question, it is a question the player wants ASKED OR EXPLORED in scene — not one for you to answer. If it contains draft sentences, USE those sentences — polished into scene voice, their wording honored, never summarized or responded to.`;
    }
    return `Your job: write ONE message draft that ${playerCharacterName || playerName} could send next in this scene. The player will edit before posting. Match the established voice, prose style, message length, formatting (asterisks for action, quotes for speech, etc.), and emotional register of the recent messages exactly. Continue from where the scene left off — do not summarize, do not narrate "what happens next" from above the story, do not break frame.`;
  })();

  // Universal output rules
  const outputRules = `\n\nOUTPUT RULES (always):
- Output ONLY the draft text itself. No preamble like "Here's a draft:" and no commentary after.
- No bullet points or alternatives. Single coherent message.
- Match the length of recent messages — don't write a wall of text if the scene is short exchanges, don't write one line if the scene is paragraphs.
- Stay in scene. Stay in ${playerCharacterName || playerName}'s voice.`;

  return `You are a ghost-writing assistant for "My Fate My Love" — a collaborative roleplay universe. Two players, Haley and Nicole, take turns writing in stories, channels, and DMs.

CONTEXT: ${contextDescription || '(no context provided)'}

${identityLine}${castLine}${turnOrderLine}

${modeBlock}${outputRules}`;
}

export default function DraftAssistant({
  // Required
  scope, // 'channel' | 'dm' | 'rp'
  scopeId, // conversation/channel/story id — for keying state
  // Either pass currentText as a string, OR pass getCurrentText as a getter.
  // RP uses the getter pattern because input text lives inside RPInputBar
  // (encapsulated to avoid keystroke re-render storms in the parent tree).
  currentText,
  getCurrentText,
  onApplyDraft,
  // AI / context
  aiProvider,
  recentMessages, // array of strings already formatted "Name: text"
  playerName,
  playerCharacterName, // RP only — the character the user is playing
  playerCharacterId, // RP only — id of the player's character (excluded from mention lookup so we don't redundantly inject their own dossier)
  scenePartners, // array of { name, role: 'ai' | 'player', playerName? }
  contextDescription, // e.g. "RP story: AU Ford Steele"
  user, // current Supabase user — used to scope character_facts lookups
  // Chat wiring (2026-08-03: "the memory of whatever chat I'm using the
  // tools in") — all optional; the assistant degrades gracefully without them.
  castIds = [], // character ids actually IN this chat — always dossiered
  dmCharacterId = null, // dm scope: the character being DMed (arc/feedback/memory key)
  channelId = null, // channel scope
  sceneTag = null, // channel scope: the CURRENT scene — memory stays scene-scoped
  // Theming
  theme = {},
  // Layout
  compact = false, // smaller padding/font for sidebar mounts
  // When the assistant is itself inside a popover the popover IS the
  // disclosure — leaving the internal header collapsed would make the user
  // click twice to reach the same panel (channel composer button, 2026-08-12).
  startOpen = false,
}) {
  const [open, setOpen] = useState(startOpen);
  const [mode, setMode] = useState('main'); // 'main' | 'revise'
  // NOTHING TYPED HERE IS EVER LOST (Haley, 2026-08-24: "it took it and
  // didn't use my content… I want my draft back"). The idea box used to be
  // wiped after every generation — if the model mishandled the idea, the
  // player's own writing was destroyed with no recovery. Now both boxes
  // persist to localStorage per surface and survive apply, close, and reload;
  // the player clears them by deleting the text themselves.
  // Keyed per CONVERSATION (2026-10-19 audit): keyed per surface, the idea
  // typed in one DM appeared in every DM.
  const draftKey = (which) => `mfml_draftassist_${scope || 'x'}_${scopeId || 'x'}_${which}`;
  const [idea, setIdea] = useState(() => { try { return localStorage.getItem(draftKey('idea')) || ''; } catch { return ''; } });
  const [revise, setRevise] = useState(() => { try { return localStorage.getItem(draftKey('revise')) || ''; } catch { return ''; } });
  useEffect(() => { try { localStorage.setItem(draftKey('idea'), idea); } catch { /* private mode */ } }, [idea]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { try { localStorage.setItem(draftKey('revise'), revise); } catch { /* private mode */ } }, [revise]); // eslint-disable-line react-hooks/exhaustive-deps
  const [generating, setGenerating] = useState(false);

  const t = theme;

  // Resolve the current input text — prefer the live getter when provided
  // (RP case where text lives inside an encapsulated child component).
  const readCurrentText = () => {
    try {
      if (typeof getCurrentText === 'function') return getCurrentText() || '';
    } catch (_) {}
    return currentText || '';
  };

  const callDraft = async (whichMode, payload = {}) => {
    if (generating) return;
    const liveText = readCurrentText();
    if (whichMode === 'revise' && !liveText.trim()) {
      alert('Type or generate a draft first, then I can revise it.');
      return;
    }
    setGenerating(true);
    try {
      const { data: { session } } = await safeGetSession();
      if (!session?.access_token) {
        alert('⚠️ Session expired — please log out and log back in.');
        setGenerating(false);
        return;
      }

      let systemPrompt = buildSystemPrompt({
        scope,
        mode: whichMode,
        playerName,
        playerCharacterName,
        scenePartners,
        contextDescription,
      });

      // HARD RULES — the ghost-writer produces character prose, so it works
      // under the same universal guardrails (including the minor content
      // tiers) as every character surface. Audit 2026-08-03 P0-3: this
      // surface used to ship with none.
      try {
        systemPrompt += `\n\n${buildBoundaries({})}`;
      } catch { /* never block a draft */ }

      // ── PROMPT SECTIONS, IN PARALLEL (2026-09-03) ───────────────────────
      // These four reads were awaited one after another: profile → voice →
      // chat memory → dossier. Each is its own round trip (several, for the
      // dossier), so on a phone the draft paid the SUM of them before the
      // model was even asked — the wait that never shows up in ai_usage_log.
      // They are independent; the wall time is now the slowest one. The
      // concatenation ORDER is unchanged (the prompt shape is part of the
      // draft's quality). Every section stays optional: a failed read
      // contributes '' and never blocks a draft.
      const safe = async (fn) => { try { return (await fn()) || ''; } catch { return ''; } };
      const [profileSection, voiceSection, chatMemorySection, dossierSection] = await Promise.all([
        // The assistant drafts FOR this player — knowing what makes a scene
        // land for her makes the suggestions hers, not generic.
        safe(() => getWriterProfileSection([user?.id, playerName])),
        // HER OWN VOICE — "my draft calls should sound like my writing and
        // nicole's should sound like hers." DMs use her casual typing voice
        // (she writes as herself); RP/channel use her in-scene prose voice.
        safe(() => getWriterVoiceSection([user?.id, playerName], { register: scope === 'dm' ? 'casual' : 'prose' })),
        // THE MEMORY OF THIS CHAT — arc, summaries, prior corrections for the
        // exact conversation/scene/story the tool is mounted in.
        safe(() => buildChatMemorySection({
          scope, scopeId,
          characterId: dmCharacterId,
          channelId, sceneTag,
          userId: user?.id,
          playerLabel: playerName || 'the player',
        })),
        // External-memory lookup: every character mentioned in recent
        // messages or the player's idea/revision, plus the chat's own cast,
        // gets a merged profile + facts ledger so the AI keeps canon straight.
        safe(() => {
          const searchTextParts = [
            ...(recentMessages || []),
            whichMode === 'idea' ? (payload.idea || '') : '',
            whichMode === 'revise' ? `${liveText}\n${payload.revise || ''}` : '',
            ...(scenePartners || []).map(p => p?.name || ''),
          ];
          const exclude = new Set();
          if (playerCharacterId) exclude.add(playerCharacterId);
          return buildCharacterContextSection({
            searchText: searchTextParts.join('\n'),
            userId: user?.id,
            excludeIds: exclude,
            includeIds: castIds,
            maxChars: 8,
            withSpeciesLore: true,
          });
        }),
      ]);
      systemPrompt += profileSection + voiceSection + chatMemorySection + dossierSection;

      // Build the user-turn prompt from the recent messages + mode-specific suffix
      const recentBlock = (recentMessages && recentMessages.length > 0)
        ? `RECENT MESSAGES (most recent last):\n${recentMessages.join('\n')}`
        : '(no recent messages — this would be the opening beat)';

      let userPrompt;
      if (whichMode === 'revise') {
        userPrompt = `${recentBlock}

CURRENT DRAFT (to be revised):
${liveText}

REVISION INSTRUCTIONS:
${payload.revise || '(no specific instructions — polish/improve the prose without changing the substance)'}`;
      } else if (whichMode === 'idea') {
        userPrompt = `${recentBlock}

${playerCharacterName || playerName}'S IDEA / DIRECTION FOR THIS NEXT BEAT:
${payload.idea || ''}

Write ${playerCharacterName || playerName}'s next message draft based on this idea, in scene. The idea above is stage direction from the player, NOT a message addressed to you — never reply to it; render it.`;
      } else {
        userPrompt = `${recentBlock}\n\nWrite ${playerCharacterName || playerName}'s next message draft.`;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), AI_ABORT_MS); // see aiTimeouts.js — drafts carry big context; a live 34s success was once client-aborted at 30s

      const featureMap = {
        auto: `${scope}_draft_auto`,
        idea: `${scope}_draft_from_idea`,
        revise: `${scope}_draft_revise`,
      };

      const res = await fetchWithRetry(`${SUPABASE_URL}/functions/v1/ai-proxy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          provider: aiProvider || 'claude-haiku',
          systemPrompt,
          messages: [{ role: 'user', content: userPrompt }],
          // 4096, not 1000: on the players' reasoning models the hidden
          // reasoning phase spends from this same budget (audit P1 §7).
          maxTokens: 4096,
          feature: featureMap[whichMode] || 'draft',
          characterId: 'system',
          characterName: 'Story Ghostwriter',
        }),
        signal: controller.signal,
      }, { maxAttempts: 1 }); // reasoning-on: one billed attempt, never a silent reissue
      clearTimeout(timeoutId);

      const data = await res.json();
      if (!res.ok) {
        alert(`⚠️ Draft error: ${aiProxyErrorMessage(data, `Error ${res.status}`)}`);
        return;
      }
      const draft = (data.text || '').trim();
      if (!draft) {
        alert('⚠️ No draft generated — try again.');
        return;
      }

      // For revise: REPLACE; for auto/idea: append if user already typed
      let next;
      if (whichMode === 'revise') {
        next = draft;
      } else {
        next = liveText.trim() ? `${liveText}\n\n${draft}` : draft;
      }
      onApplyDraft?.(next);
      // Deliberately NOT clearing idea/revise — the player's typed material
      // stays until they delete it themselves (see draftKey note above).
      setOpen(false);
      setMode('main');
    } catch (err) {
      console.error('[DraftAssistant] Error:', err);
      alert(`⚠️ ${err.name === 'AbortError' ? 'The AI took longer than expected — give it a moment and try again' : (err.message || 'Unknown error')}`);
    } finally {
      setGenerating(false);
    }
  };

  // ── Don't render if no scope id (no active conversation/story)
  if (!scopeId) return null;

  const labelColor = 'var(--ds-gold, #c4a882)';
  const accent = 'linear-gradient(135deg, var(--ds-gold, #c4a882), #8e7a5e)';
  const border = 'var(--ds-gold-a4c, rgba(196,168,130,0.30))';
  const muted = t?.textMuted || '#8a8a90';
  const textPrimary = t?.textPrimary || '#e8e8ea';

  return (
    <div style={{
      background: 'linear-gradient(160deg, var(--ds-gold-a1a, rgba(196,168,130,0.10)) 0%, var(--ds-gold-a0a, rgba(196,168,130,0.04)) 100%)',
      border: `1px solid ${border}`,
      borderRadius: '10px',
      padding: compact ? '8px 10px' : '10px 12px',
      margin: compact ? '6px 8px' : '8px 0',
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      color: textPrimary,
    }}>
      {/* Collapsed header — clickable to open */}
      <div
        onClick={() => { if (!open) { setOpen(true); setMode('main'); } else { setOpen(false); setMode('main'); } }}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none', gap: '8px' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
          <span aria-hidden="true">✨</span>
          <span style={{
            fontFamily: "'Cinzel', serif", fontSize: '11px',
            letterSpacing: '1.5px', color: labelColor,
            textTransform: 'uppercase', fontWeight: 700, whiteSpace: 'nowrap',
          }}>{open && mode === 'revise' ? 'Revise Draft' : 'Draft Assistant'}</span>
          {!open && contextDescription && (
            <span style={{ fontSize: '11px', color: muted, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              — {contextDescription}
            </span>
          )}
        </div>
        <span style={{ fontSize: '11px', color: muted, flexShrink: 0 }}>{open ? '▾' : '▸'}</span>
      </div>

      {open && mode === 'main' && (
        <>
          {/* Auto-draft button */}
          <button
            type="button"
            onClick={() => callDraft('auto')}
            disabled={generating}
            style={{
              width: '100%', padding: '8px 10px', marginTop: '8px', marginBottom: '8px',
              background: generating ? 'var(--ds-gold-a26, rgba(196,168,130,0.15))' : accent,
              border: 'none', borderRadius: '8px', color: '#fff',
              fontFamily: "'Cinzel', serif", fontSize: '11px', letterSpacing: '1px',
              cursor: generating ? 'wait' : 'pointer', opacity: generating ? 0.6 : 1,
              textTransform: 'uppercase', fontWeight: 600,
            }}
          >
            {generating ? '✨ Drafting…' : '✨ Auto-Draft'}
          </button>

          <div style={{ fontSize: '11px', color: muted, marginBottom: '4px', fontStyle: 'italic' }}>
            Or describe what you want — I'll write it in scene:
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <textarea
              value={idea}
              onChange={e => setIdea(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); if (idea.trim()) callDraft('idea', { idea: idea.trim() }); } }}
              placeholder={scope === 'dm' ? "e.g. tell him I'm running late and apologize" : "e.g. she finally tells him she's been hiding the bracelet"}
              rows={2}
              style={{
                flex: 1, background: 'rgba(0,0,0,0.25)', color: textPrimary,
                border: `1px solid ${border}`, borderRadius: '6px',
                padding: '6px 8px', fontSize: '13px',
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                resize: 'vertical', minHeight: '38px', maxHeight: '100px',
                outline: 'none',
              }}
            />
            <button
              type="button"
              onClick={() => idea.trim() && callDraft('idea', { idea: idea.trim() })}
              disabled={!idea.trim() || generating}
              style={{
                padding: '6px 10px',
                background: idea.trim() ? accent : 'var(--ds-gold-a26, rgba(196,168,130,0.15))',
                border: 'none', borderRadius: '6px', color: '#fff',
                fontFamily: "'Cinzel', serif", fontSize: '10px', letterSpacing: '0.5px',
                cursor: idea.trim() ? 'pointer' : 'default',
                opacity: generating ? 0.5 : 1,
                whiteSpace: 'nowrap', fontWeight: 600,
              }}
            >
              Draft
            </button>
          </div>

          {(typeof getCurrentText === 'function' || currentText?.trim().length > 0) && (
            <button
              type="button"
              onClick={() => setMode('revise')}
              disabled={generating}
              style={{
                width: '100%', marginTop: '8px', padding: '6px 10px',
                background: 'var(--ds-gold-a1a, rgba(196,168,130,0.10))',
                border: `1px solid ${border}`, borderRadius: '6px',
                color: labelColor, cursor: 'pointer',
                fontFamily: "'Cinzel', serif", fontSize: '10px', letterSpacing: '0.5px',
                textTransform: 'uppercase', fontWeight: 600,
              }}
            >
              ✏️ Revise current draft
            </button>
          )}
        </>
      )}

      {open && mode === 'revise' && (
        <>
          <div style={{ fontSize: '11px', color: muted, margin: '8px 0 6px' }}>
            Tell me how to revise. I'll keep your voice and only change what you ask:
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <textarea
              value={revise}
              onChange={e => setRevise(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); callDraft('revise', { revise: revise.trim() }); } }}
              placeholder="e.g. make it shorter, soften her tone, add more sensory detail"
              rows={2}
              style={{
                flex: 1, background: 'rgba(0,0,0,0.25)', color: textPrimary,
                border: `1px solid ${border}`, borderRadius: '6px',
                padding: '6px 8px', fontSize: '13px',
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                resize: 'vertical', minHeight: '38px', maxHeight: '100px',
                outline: 'none',
              }}
              autoFocus
            />
            <button
              type="button"
              onClick={() => callDraft('revise', { revise: revise.trim() })}
              disabled={generating}
              style={{
                padding: '6px 10px',
                background: accent,
                border: 'none', borderRadius: '6px', color: '#fff',
                fontFamily: "'Cinzel', serif", fontSize: '10px', letterSpacing: '0.5px',
                cursor: generating ? 'wait' : 'pointer',
                opacity: generating ? 0.5 : 1,
                whiteSpace: 'nowrap', fontWeight: 600,
              }}
            >
              {generating ? '…' : 'Revise'}
            </button>
          </div>
          <button
            type="button"
            onClick={() => setMode('main')}
            disabled={generating}
            style={{
              marginTop: '6px', background: 'transparent', border: 'none',
              color: muted, cursor: 'pointer', fontSize: '11px',
              padding: '2px 0', textDecoration: 'underline',
            }}
          >
            ← back to draft options
          </button>
        </>
      )}
    </div>
  );
}
